/**
 * TaskListByClient - Versão simplificada para contexto de cliente
 *
 * Este componente foca em mostrar as tarefas essenciais do cliente de forma
 * simples e direta, sem tentar replicar o Kanban completo.
 */

import { useState, useEffect, useCallback } from 'react';
import { Task, TaskStatus } from '@/types/tasks';
import { taskService } from '@/services/task-service';
import { TaskCard } from './TaskCard';
import { useTaskDrawer } from '@/contexts/TaskDrawerContext';
import { Loader2, AlertCircle, ExternalLink, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import toast from '@/lib/toast';
import { useNavigate } from 'react-router-dom';
import { isTaskDueToday, isTaskOverdue } from '@/utils/task-date';

import { getErrorMessage } from '../../utils/error.utils'
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';
export interface TaskListByClientProps {
  clientId: string;
  onTaskClick?: (task: Task) => void;
  refreshTrigger?: number;
  /** Dossiê: sem os contadores, só a lista e um link para o quadro. */
  compact?: boolean;
}

// Helper: check if task is overdue
function isOverdue(task: Task): boolean {
  return isTaskOverdue(task);
}

// Helper: check if task is due today
function isDueToday(task: Task): boolean {
  return isTaskDueToday(task);
}

export function TaskListByClient({
  clientId,
  onTaskClick,
  refreshTrigger,
  compact = false
}: TaskListByClientProps) {
  const navigate = useNavigate();
  const drawer = useTaskDrawer();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load tasks
  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await taskService.getByClient(clientId);

      if (response && response.data) {
        setTasks(response.data.tasks || []);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar tarefas'));
      toast.error('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks, refreshTrigger, drawer.version]);

  // Handle status change
  const handleStatusChange = async (taskId: number, newStatus: string) => {
    try {
      if (newStatus === 'COMPLETED') {
        await taskService.complete(taskId);
      } else {
        await taskService.update(taskId, { status: newStatus as TaskStatus });
      }

      toast.success('Status atualizado');
      loadTasks();
    } catch (err: unknown) {
      toast.error('Erro ao atualizar status da tarefa');
    }
  };

  // Calculate simple metrics
  const overdueTasks = tasks.filter(isOverdue);
  const todayTasks = tasks.filter(isDueToday);
  const totalTasks = tasks.length;

  // Group tasks for display
  const groupedTasks = {
    overdue: overdueTasks,
    today: todayTasks,
    upcoming: tasks.filter(t => !isOverdue(t) && !isDueToday(t) && t.status !== 'COMPLETED')
  };

  const renderTasks = (list: Task[]) => (
    <div className="flex flex-col gap-3">
      {list.map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          onClick={() => (onTaskClick ? onTaskClick(task) : drawer.openTask(task.id))}
          onStatusChange={handleStatusChange}
          showClient={false}
          showCase={true}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" aria-busy="true">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-label="Carregando tarefas" />
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
        <AlertCircle className="mx-auto mb-2 size-5 text-destructive" aria-hidden="true" />
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={loadTasks}>Tentar novamente</Button>
      </div>
    );
  }

  const kpis: { label: string; value: number; icon?: typeof Clock; alert?: boolean }[] = [
    { label: 'Total', value: totalTasks },
    { label: 'Atrasadas', value: overdueTasks.length, icon: AlertTriangle, alert: overdueTasks.length > 0 },
    { label: 'Hoje', value: todayTasks.length, icon: Clock }
  ];

  if (compact && totalTasks === 0) {
    return <p className="text-sm text-muted-foreground">Nenhuma tarefa para este cliente.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {compact ? (
        <Button variant="link" size="sm" className="-mt-2 self-end px-0" onClick={() => navigate(`/tasks?vista=tabela&cliente=${clientId}`)}>
          Ver todas no quadro <ExternalLink className="size-4" />
        </Button>
      ) : (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="grid flex-1 grid-cols-3 gap-3">
          {kpis.map(({ label, value, icon: Icon, alert }) => (
            <div
              key={label}
              className={cn('rounded-lg border bg-card p-3 text-center', alert && 'border-destructive/30 bg-destructive/5')}
            >
              <div className={cn('text-2xl font-semibold tabular-nums', alert && 'text-destructive')}>{value}</div>
              <div className={cn('mt-1 flex items-center justify-center gap-1 text-xs', alert ? 'text-destructive' : 'text-muted-foreground')}>
                {Icon && value > 0 && <Icon className="size-3" aria-hidden="true" />}
                <span>{label}</span>
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={() => navigate(`/tasks?vista=tabela&cliente=${clientId}`)}
          className="self-start sm:self-auto"
          title="Abrir no Kanban com filtro deste cliente"
        >
          Ver no Kanban
          <ExternalLink className="size-4" />
        </Button>
      </div>
      )}

      {totalTasks === 0 && (
        <div className="flex flex-col items-center gap-1 rounded-lg border bg-muted/40 py-12 text-center">
          <CheckCircle2 className="mb-2 size-10 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium">Nenhuma tarefa para este cliente</p>
          <p className="text-sm text-muted-foreground">Crie uma nova tarefa para começar</p>
        </div>
      )}

      {overdueTasks.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="size-4 text-destructive" aria-hidden="true" />
            <h3 className="text-base font-semibold text-destructive">
              {overdueTasks.length} {overdueTasks.length === 1 ? 'tarefa atrasada' : 'tarefas atrasadas'}
            </h3>
          </div>
          {renderTasks(overdueTasks)}
        </section>
      )}

      {todayTasks.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <Clock className="size-4 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-base font-semibold">
              {todayTasks.length} {todayTasks.length === 1 ? 'tarefa vence hoje' : 'tarefas vencem hoje'}
            </h3>
          </div>
          {renderTasks(todayTasks)}
        </section>
      )}

      {groupedTasks.upcoming.length > 0 && (
        <section>
          <h3 className="mb-3 text-base font-semibold">Próximas tarefas</h3>
          {renderTasks(groupedTasks.upcoming)}
        </section>
      )}
    </div>
  );
}
